import { useTranslation } from 'react-i18next';
import { use, useState } from 'react';
import { TeContainer, TeContainerSlim, TeDivider, TeText } from '@topcon/tuicl-react';

import { ReviewCompanyCreationRequestStepActions } from './ReviewCompanyCreationRequestStepActions';
import { ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';
import { ServiceAgreementTermsEnum } from '../../../api/generated/pm';
import SelectableAgreementTypeCard from '../../ServiceAgreement/SelectableAgreementTypeCard';
import { AgreementType } from '../../../types/customer-modal.types';
import { useAppData } from '../../../hooks/queries/appSession.queries';
import ServiceAgreementAcknowledgements from '../../ServiceAgreement/ServiceAgreementAcknowledgements';

interface ReviewRequestBusinessPartnerAgreementTypeStepProps {
  id: string;
  visible: boolean;
  onNextStep: () => void;
  onPreviousStep?: () => void;
  agreementTerm: ServiceAgreementTermsEnum;
}

function ReviewRequestBusinessPartnerAgreementTypeStep({
  visible,
  onNextStep,
  onPreviousStep,
  id,
  agreementTerm,
}: ReviewRequestBusinessPartnerAgreementTypeStepProps) {
  const { t } = useTranslation();
  const { isDealer } = useAppData();
  const { setResult } = use<ReviewResultContextType>(ReviewResultContext);
  const [acknowledged, setAcknowledged] = useState<boolean>(false);

  if (!visible) return;

  const handleReject = () => {
    setResult('declined');
  };

  return (
    <TeContainer justifyContent="space-between" fill padding={0} gap={0} column>
      <TeContainerSlim column fill padding={'m'} gap="xs2">
        <TeText weight={700} size={20}>
          {t('serviceAgreement.agreementReview.title')}
        </TeText>
        <TeText color="text.secondary" size={14}>
          {t('serviceAgreement.agreementReview.description')}
        </TeText>
      </TeContainerSlim>
      <TeDivider dividerColor="border.secondary" />
      <TeContainer grow gap={'s'} padding={'m'}>
        <SelectableAgreementTypeCard
          title={t('serviceAgreement.full_service')}
          agreementType={AgreementType.FullService}
          isActive={agreementTerm === ServiceAgreementTermsEnum.FULL_SERVICE}
          disabled={agreementTerm !== ServiceAgreementTermsEnum.FULL_SERVICE}
          badgeLabel={t('requested')}
        />
        <SelectableAgreementTypeCard
          title={t('serviceAgreement.limited_service')}
          agreementType={AgreementType.LimitedService}
          isActive={agreementTerm === ServiceAgreementTermsEnum.PARTIAL_SERVICE}
          disabled={agreementTerm !== ServiceAgreementTermsEnum.PARTIAL_SERVICE}
          badgeLabel={t('requested')}
        />
      </TeContainer>
      <TeDivider dividerColor="border.secondary" />
      <ServiceAgreementAcknowledgements
        side={isDealer ? 'dealer' : 'customer'}
        tier={agreementTerm}
        onAcknowledged={setAcknowledged}
      />
      <TeDivider dividerColor="border.secondary" />
      <ReviewCompanyCreationRequestStepActions
        step="agreementReview"
        id={id}
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        onReject={handleReject}
        isNextStepDisabled={!acknowledged}
      />
    </TeContainer>
  );
}

export default ReviewRequestBusinessPartnerAgreementTypeStep;
